import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { BfModal, BfDialog, BfButton, BfTextInput, BfSpinner, BfExpandingButton } from 'src/components/Butterfly';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem.vue';
import RadioButtons from 'src/components/widgets/RadioButtons.vue';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import ModalSection from './EmailDigestModalSection.vue';
import { CLEAR_ERRORS } from 'src/store/types';
import { Form, Field, defineRule } from 'components/forms';
import ProjectAPI from 'src/api/project';
import { DefaultSegmentConfig, FormOptions, guessTimezone, isSegmentValid } from './EmailDigestModal.utils';
import Utils from 'src/utils/general';
import { email } from '@vee-validate/rules';
import { rawTimeZones } from '@vvo/tzdb';
import dayjs from 'dayjs';
defineRule('email-list', (value) => {
    if (!value)
        return true;
    const emails = value.split(',');
    return emails.every((e) => {
        return email(e);
    });
});
let segmentID = 0;
const EmailDigestModal = Vue.extend({
    components: {
        BfModal,
        BfDialog,
        BfButton,
        BfExpandingButton,
        BfTextInput,
        BfSpinner,
        Dropdown,
        DropdownItem,
        Form,
        Field,
        RadioButtons,
        ModalSection,
    },
    provide() {
        return {
            getBounds: this.getBounds,
        };
    },
    props: {
        visible: { type: Boolean, default: false, required: false },
        dashboard: { type: Object, required: true },
        sortedSegmentsForFieldsLimited: { type: Object, required: true },
        savedQueries: { type: Array, required: true },
        hasNps: { type: Boolean, default: false, required: false },
        hasSentiment: { type: Boolean, default: false, required: false },
        savedData: { type: Object, required: false, default: null },
    },
    data() {
        return {
            serverErrors: [],
            segments: [],
            options: FormOptions,
            recipientInput: '',
            emailError: '',
            page: 0,
            loadingPreview: false,
            previewHTML: '',
            sendingTest: false,
            testSent: false,
            digestSendTime: this.savedData?.send_date ? dayjs(this.savedData.send_date) : dayjs(),
            datePickerOptions: {
                disabledDate(date) {
                    return dayjs(date).isSameOrBefore(dayjs());
                },
            }
        };
    },
    computed: {
        ...mapGetters([
            'currentDashboard', 'currentUser',
        ]),
        segmentsAreValid() {
            // At least one segment is required
            const noneEnabled = !this.segments.find((s) => s.enabled);
            if (noneEnabled)
                return false;
            for (const segment of this.segments) {
                if (segment.enabled) {
                    if (!isSegmentValid(segment))
                        return false;
                }
            }
            return true;
        },
        isAlive() {
            return !this._isDestroyed && !this._isBeingDestroyed;
        },
        selectedSegments() {
            return this.segments
                .filter((segment) => segment.enabled)
                .map((segment) => {
                const props = {
                    type: segment.type,
                };
                if (segment.type === 'metric') {
                    props.metrics = segment.metrics;
                }
                if (segment.type === 'theme') {
                    props.queries = segment.queries;
                    props.metric = segment.metric;
                    props.highlightOutliers = segment.highlightOutliers;
                }
                if (segment.type === 'mostChangedThemes') {
                    props.metric = segment.metric;
                    props.order = segment.order;
                    props.limit = segment.limit;
                    props.highlightOutliers = segment.highlightOutliers;
                    props.returnSummary = segment.returnSummary;
                    props.showSampleVerbatims = segment.showSampleVerbatims;
                }
                if (segment.type === 'verbatim') {
                    props.queries = segment.queries;
                    props.numberVerbatims = segment.numberVerbatims;
                    props.includeSummary = segment.includeSummary;
                }
                if (segment.type === 'custom') {
                    props.field = segment.field;
                    props.metric = segment.metric;
                    props.segments = segment.segments;
                }
                return props;
            });
        },
        initialFormValues() {
            const recipients = this.savedData?.recipients
                .filter(({ subscribed }) => subscribed)
                .map(({ email }) => email)
                .join(',');
            const timezone = rawTimeZones.find((zone) => zone.name === this.savedData?.timezone);
            const today = dayjs().format('YYYY-MM-DD');
            return {
                subject: this.savedData?.subject ?? '',
                description: this.savedData?.description ?? '',
                recipients: recipients ?? '',
                frequency: this.savedData?.frequency ?? this.options.frequency[0].value,
                comparison_interval: this.savedData?.comparison_interval ?? this.options.comparison_interval[0].value,
                day: this.savedData?.day ?? 0,
                hour: this.options.hour[this.savedData?.hour ?? 0],
                send_date: this.savedData?.send_date ?? today,
                timezone: timezone ?? guessTimezone(),
            };
        },
        editMode() {
            return this.savedData !== null;
        },
    },
    watch: {
        visible(newValue) {
            if (newValue) {
                this.resetModal();
            }
        },
    },
    methods: {
        ...mapActions({ CLEAR_ERRORS }),
        getBounds() {
            if (this.page === 0)
                return this.$refs.scrollareaPage0;
            return this.$refs.scrollareaPage1;
        },
        close() {
            this.resetModal();
            this.$emit('close');
        },
        resetModal() {
            this.goToPage(0);
            this.recipientInput = '';
            this.segments = [];
            const findSegments = (type) => {
                const segments = this.savedData?.segments.filter((s) => s.type === type);
                if (segments?.length) {
                    segments.forEach((segment) => {
                        segment.enabled = true;
                    });
                    return segments;
                }
                return null;
            };
            const metricSegments = findSegments('metric');
            metricSegments
                ? this.segments.push(...metricSegments)
                : this.addNewSegment('metric', false);
            const sentimentSegments = findSegments('sentiment');
            sentimentSegments
                ? this.segments.push(...sentimentSegments)
                : this.addNewSegment('sentiment', false);
            const themeSegments = findSegments('theme');
            themeSegments
                ? this.segments.push(...themeSegments)
                : this.addNewSegment('theme', false);
            const mostChangedThemesSegments = findSegments('mostChangedThemes');
            mostChangedThemesSegments
                ? this.segments.push(...mostChangedThemesSegments)
                : this.addNewSegment('mostChangedThemes', false);
            const verbatimSegments = findSegments('verbatim');
            verbatimSegments
                ? this.segments.push(...verbatimSegments)
                : this.addNewSegment('verbatim', false);
            const customSegments = findSegments('custom');
            customSegments
                ? this.segments.push(...customSegments)
                : this.addNewSegment('custom', false);
        },
        addNewSegment(type, enabled = true, animated = false) {
            const segment = Utils.cloneDeep({
                ...DefaultSegmentConfig[type].config,
                id: segmentID++,
                enabled,
            });
            this.segments.push(segment);
            if (animated) {
                this.$nextTick(() => {
                    this.$refs?.addSectionButton?.$el.scrollIntoView({ behavior: 'smooth' });
                });
            }
        },
        processFormData(formValues) {
            const recipients = formValues.recipients.split(',')
                .map((email) => ({
                subscribed: true,
                email,
            }));
            // Preserve unsubscribed recipients
            if (Array.isArray(this.savedData?.recipients)) {
                this.savedData.recipients.forEach((recipient) => {
                    if (!recipients.find(({ email }) => email === recipient.email)) {
                        recipients.push({ email: recipient.email, subscribed: false });
                    }
                });
            }
            const daySend = dayjs(this.digestSendTime).date() ?? 0;
            const today = dayjs().format('YYYY-MM-DD');
            const send_date = dayjs(this.digestSendTime).format('YYYY-MM-DD');
            return {
                ...formValues,
                send_date: send_date ?? today,
                day: daySend,
                hour: formValues.hour.value,
                segments: this.selectedSegments,
                recipients,
                timezone: formValues.timezone.name,
            };
        },
        makeTimeZoneLabel(timezone) {
            function toHoursAndMinutes(totalMinutes) {
                function padTo2Digits(num) {
                    return num.toString().padStart(2, '0');
                }
                const minutes = totalMinutes % 60;
                const hours = Math.floor(totalMinutes / 60);
                const sign = totalMinutes >= 0 ? '+' : '';
                return `${sign}${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;
            }
            if (timezone === undefined)
                return '';
            return `(GMT${toHoursAndMinutes(timezone.rawOffsetInMinutes)}) ${timezone.alternativeName} - ${timezone.mainCities[0]}`;
        },
        processErrors(errors) {
            let errorList = [];
            if (Object.keys(errors).every((key) => Array.isArray(errors[key]))) {
                // If all values in errors are arrays, use reduce
                errorList = Object.keys(errors).reduce((errorList, key) => {
                    const keyErrors = errors[key].map((error) => `${key}: ${error}`);
                    return errorList.concat(keyErrors);
                }, []);
            }
            else {
                // Otherwise, use map
                errorList = Object.keys(errors).map((key) => {
                    return `${key}: ${errors[key]}`;
                });
            }
            this.serverErrors = errorList;
        },
        async createEmailDigest(formValues) {
            this.CLEAR_ERRORS();
            const data = this.processFormData(formValues);
            this.$analytics.track.dashboard.digest.created(this.dashboard.id);
            data.send_date = dayjs(data.send_date).format('YYYY-MM-DD');
            try {
                const res = await ProjectAPI.createEmailDigest(this.dashboard.id, data);
                if (res.ok && this.isAlive) {
                    this.$emit('refresh-digests');
                    this.close();
                }
            }
            catch (e) {
                if (!this.isAlive)
                    return;
                if (e.status === 400) {
                    this.CLEAR_ERRORS();
                    this.processErrors(e.body);
                }
            }
        },
        trackOutlier(val) {
            this.$analytics.track.dashboard.digest.outlierSettingChanged(this.dashboard.id, val);
        },
        trackSummary(val) {
            this.$analytics.track.dashboard.digest.summarySettingChanged(this.dashboard.id, val);
        },
        async updateDigest(formValues) {
            this.CLEAR_ERRORS();
            this.$analytics.track.dashboard.digest.updated(this.savedData.id, this.dashboard.id);
            const data = this.processFormData(formValues);
            try {
                const res = await ProjectAPI.updateEmailDigest(this.dashboard.id, this.savedData.id, data);
                if (res.ok && this.isAlive) {
                    this.$emit('refresh-digests');
                    this.close();
                }
            }
            catch (e) {
                if (!this.isAlive)
                    return;
                if (e.status === 400) {
                    this.CLEAR_ERRORS();
                    this.processErrors(e.body);
                }
            }
        },
        async getEmailDigestPreview() {
            this.CLEAR_ERRORS();
            const formRef = this.$refs.form;
            const formData = formRef.getValues();
            const data = this.processFormData(formData);
            this.previewHTML = '';
            this.loadingPreview = true;
            try {
                const res = await ProjectAPI.getEmailDigestPreview(this.dashboard.id, data, this.savedData?.id);
                if (res.ok && this.isAlive) {
                    this.previewHTML = res.data;
                }
            }
            catch (e) {
                if (!this.isAlive)
                    return;
                if (e.status === 400) {
                    this.CLEAR_ERRORS();
                    this.processErrors(e.body);
                }
            }
            finally {
                this.loadingPreview = false;
            }
        },
        addRecipient(currentValues, setFieldValue) {
            if (!this.recipientInput)
                return;
            const recipients = currentValues.recipients.split(',').filter(Boolean);
            if (recipients.includes(this.recipientInput))
                return;
            const newValue = recipients.concat(this.recipientInput).join(',');
            setFieldValue('recipients', newValue);
            this.recipientInput = '';
        },
        removeRecipient(field, i, setFieldValue) {
            const values = field.value.split(',').slice();
            values.splice(i, 1);
            const newValue = values.join(',');
            setFieldValue(field.name, newValue);
        },
        validateEmail(str) {
            this.emailError = email(str) ? '' : 'Please enter a valid email.';
        },
        goToPage(page) {
            if (!this.segmentsAreValid)
                return;
            if (page === this.page)
                return;
            if (page === 1) {
                this.getEmailDigestPreview();
            }
            this.page = page;
        },
        async sendTestEmail() {
            this.CLEAR_ERRORS();
            if (this.sendingTest || this.testSent) {
                return;
            }
            this.sendingTest = true;
            const formRef = this.$refs.form;
            const formData = formRef.getValues();
            const data = this.processFormData(formData);
            try {
                const res = await ProjectAPI.sendTestEmail(this.dashboard.id, data, this.savedData?.id);
                if (res.ok && this.isAlive) {
                    this.testSent = true;
                    setTimeout(() => this.testSent = false, 3000);
                }
            }
            catch (e) {
                if (!this.isAlive)
                    return;
                if (e.status === 400) {
                    this.CLEAR_ERRORS();
                    this.processErrors(e.body);
                }
            }
            finally {
                this.sendingTest = false;
            }
        }
    }
});
export default EmailDigestModal;
